import { format } from 'date-fns';
import _ from 'lodash';
import { createModel } from '@rematch/core';
import { isMobile } from 'react-device-detect';
import { push } from 'connected-react-router';

import { RootModel } from '../index';
import { get, post } from '../../api/api';
import { apiUrls, replaceUrls } from '../../api/apiUrls';
import EventsState, { TreeSport, ListSport, TopFilter, Sport, SportsFastInputHistory } from '../../interfaces/EventsState';

export default createModel<RootModel>()({
  state: {
    availableSports: [],
    events: [],
    eventsTree: [],
    eventsTop: [],
    selectedEvent: {},
    numbersToBet: {
      events: [],
      regular: {},
      bonus: {},
      selectedNumbers:[],
      selectedBankers:[],
      selectedBonus: [],
      drawsSequence:1,
      permutations: { singleStake: 1000, totalStake:0 }
    },
    numbersPermutations: {},
    numberEvent: [],
    popularEvents: [],
    event: {},
    singleNumberEvent: {},
    eventSource: null,
    isSearching: false,
    loading: true,
    treeLoading: true,
    filterSports: {
      type: '1,8',
      range: 1,
      rangeHours: 3,
      level: 2,
      tournaments: [],
      top: null,
      meetings: []
    },
    filterSlider: {
      range: 4,
      rangeHours: 24,
      selectedDate: format(new Date(), 'yyyyMMdd')
    },
    horseRacingList: {},
    dataTree: {},
    searchResults: { totals: 0, list: [] },
    search: '',
    searchProduct: '',
    isDirty: false, // Use to show outside navigation Prompt
    sport: null,
    isCardCodeEvent: false,
    cardCodeErrors: '',
    cardCodes: {
      fixtures: '',
      outcomes: ''
    },
    matchTrackerSidebar: {
      showMatchTracker: false,
      matchTrackerMinimized: false,
      name: '',
      eventId: null,
      nextRaces: null
    },
    showMobileTracker: true,
    sportErrors: { betcode:'', race:'', betType:'', numbers:'', stake:'' },
    sportValues: { betCode:'', race:'CR', betType:'', numbers:'', stake:'' },
    sportFastInputHistory: [],
    liveOutcomes: [],
    shouldReloadTabs: true
  } as EventsState,

  reducers: {
    setSearch(state, payload: string) {
      return { ...state, search: payload };
    },

    setShowMobileTracker(state){
      return { ...state, showMobileTracker: !state.showMobileTracker };
    },

    setPopularEvents(state, payload: []) {
      return { ...state, popularEvents: payload };
    },

    setDirtyPage(state, payload: boolean) {
      return { ...state, isDirty: payload };
    },

    setSearching(state, payload: boolean | { status: boolean, product: string }) {
      if (_.isBoolean(payload)) {
        return { ...state, isSearching: payload, searchProduct: payload === false ? '' : state.searchProduct };
      } else {
        return { ...state, isSearching: payload.status, searchProduct: payload.product || '' };
      }
    },

    setEvent(state, payload: object) {
      return { ...state, selectedEvent: payload };
    },

    resetNumberPermutations(state) {
      return { ...state, numbersToBet: {
        ...state.numbersToBet,
        selectedNumbers:[],
        selectedBankers:[],
        selectedBonus: [],
        drawsSequence:1,
        permutations: {
          odds:0,
          maximumProfit: 0,
          permutationsCount: 0,
          totalStake:0,
          singleStake:1000,
          anyNumbers:[]
        }
      }
      };
    },

    setSportEventsTree(state, payload: TreeSport[]) {
      return {
        ...state,
        eventsTree: payload
      };
    },

    setSportEvents(state, { tree, events, top }: { tree?: TreeSport[], events: ListSport[], top?: TopFilter[] }) {
      return {
        ...state,
        events: events ? events : state.events,
        eventsTree: tree ? tree : state.eventsTree,
        eventsTop: top ? top : state.eventsTop
      };
    },

    setEventSource(state, payload: EventSource) {
      return { ...state, eventSource: payload };
    },

    setDataTree(state, payload: {}) {
      return { ...state, dataTree: { ...state.dataTree, ...payload } };
    },

    setHorseListData(state, payload: { [key: string]: object }) {
      return { ...state, horseRacingList: payload };
    },

    setSearchResults(state, payload: { totals?: number, list?: ListSport[] }) {
      return { ...state, searchResults: payload };
    },

    clearEventSource(state) {
      return { ...state, eventSource: null };
    },

    setPermutations(state, payload: object) {
      return { ...state, numbersPermutations: payload };
    },

    setSinglePagePermutation(state, payload: { singleStake:  number, totalStake: number }) {
      return { ...state, numbersToBet: { ...state.numbersToBet, permutations: payload } };
    },

    setShouldReloadTabs(state, payload:boolean){
      return {
        ...state,
        shouldReloadTabs: payload
      };
    },

    setNumberBet(state, payload: { type: string, event: object | any[] | number }) {
      return { ...state, numbersToBet: { ...state.numbersToBet, [payload.type]: payload.event } };
    },

    setFilterSlider(state, payload: { range: number, rangeHours: number, selectedDate: string }) {
      return { ...state, filterSlider: { ...state.filterSlider, ...payload } };
    },

    setLoading(state, payload: boolean) {
      return { ...state, loading: payload };
    },

    setSportEventsTreeLoading(state, payload: boolean) {
      return { ...state, treeLoading: payload };
    },

    setSportEventsLoading(state, payload: boolean) {
      return { ...state, loading: payload, treeLoading: payload };
    },

    setFilterTournaments(state, payload: string[]) {
      return {
        ...state,
        filterSports: {
          ...state.filterSports,
          tournaments: payload,
          top: null,
          level: 2
        }
      };
    },

    setFilterMeetings(state, payload: []) {
      return {
        ...state,
        filterSports: {
          ...state.filterSports,
          meetings: payload
        }
      };
    },

    setFilterLevel(state, payload: number) {
      return { ...state, filterSports: { ...state.filterSports, level: payload } };
    },

    setFilterType(state, payload: { type: string, value: number, hours: number }) {
      return {
        ...state,
        filterSports: {
          ...state.filterSports,
          type: payload.type,
          range: payload.value,
          rangeHours: payload.hours
        }
      };
    },

    setFilterHours(state, payload: { range: number, rangeHours: number }) {
      return { ...state, filterSports: { ...state.filterSports, ...payload } };
    },

    setInitialFilterSports(state) {
      return {
        ...state,
        isSearching: false,
        filterSports: {
          type: '1,8',
          range: 1,
          rangeHours: 3,
          level: 2,
          tournaments: [],
          top: null,
          meetings: []
        }
      };
    },

    setSelectedTop(state, payload: string) {
      return {
        ...state,
        filterSports: {
          ...state.filterSports,
          tournaments: [],
          top: payload,
          level: 2
        }
      };
    },

    setLiveOutcomes(state, payload: number[]) {
      return {
        ...state,
        liveOutcomes: _.compact([...state.liveOutcomes, ...payload])
      };
    },

    setUpdatedEvents(state, payload:  ListSport[]){
      return {
        ...state,
        events: payload
      };
    },

    setNewAllMarkets(state, payload:EventsState['selectedEvent']){
      return {
        ...state,
        selectedEvent: payload
      };
    },

    resetLiveOutcomes(state){
      return {
        ...state,
        liveOutcomes: []
      };
    },

    removeLiveOutcomes(state, payload:number[]){
      const newLiveOutcome = _.map(state.liveOutcomes, (outcome:number) => {
        if (!_.includes(payload, outcome)){
          return outcome;
        }
        return null;
      });

      return {
        ...state,
        liveOutcomes: _.compact(newLiveOutcome)
      };
    },

    setTop(state, payload: string) {
      return {
        ...state,
        filterSports: {
          ...state.filterSports,
          top: payload
        }
      };
    },

    setAvailableSports(state, payload: Sport[]) {
      return { ...state, availableSports: payload };
    },

    setNextRaces(state, payload: any[]) {
      return { ...state, nextRaces: payload };
    },

    setSport(state, payload: number) {
      return { ...state, sport: payload };
    },

    setCardCodeEvent(state, payload: boolean) {
      return { ...state, isCardCodeEvent: payload };
    },

    setCardCodeErrors(state, payload: string){
      return { ...state, cardCodeErrors: payload };
    },

    setCardCodes(state, payload: EventsState['cardCodes']){
      return { ...state, cardCodes: payload };
    },

    setMatchTracker(state, payload: EventsState['matchTrackerSidebar']){
      return { ...state, matchTrackerSidebar: { ...state.matchTrackerSidebar, ...payload } };
    },

    setValues(state, payload: EventsState['sportValues']){
      return { ...state, sportValues:payload };
    },

    setErrors(state, payload: EventsState['sportErrors']){
      return { ...state, sportErrors:payload };
    },

    resetRetailValues(state){
      return {
        ...state,
        sportErrors: { betcode:'', race:'', betType:'', numbers:'', stake:'' },
        sportValues: { betCode:'', race:'CR', betType:'', numbers:'', stake:'' },
        valuesReset: true
      };
    },

    setResetHistory(state){
      return {
        ...state,
        sportFastInputHistory: []
      };
    },

    resetRetailErrors(state){
      return {
        ...state,
        sportErrors: { betcode:'', race:'', betType:'', numbers:'', stake:'' }
      };
    },

    setSportHistory(state, payload: SportsFastInputHistory){
      return {
        ...state,
        sportFastInputHistory: [payload, ...state.sportFastInputHistory]
      };
    }
  },

  effects: (dispatch) => ({
    clearSelectedEvent(): void {
      dispatch.events.setEvent({});
    },

    clearSelectedTop(payload: boolean): void {
      if (payload) {
        dispatch.events.setTop(null);
      } else {
        dispatch.events.setSelectedTop(null);
      }
    },

    changeSearch(payload: string): void {
      dispatch.events.setSearch(payload);
    },

    changeIsSearching(payload): void {
      dispatch.events.setSearching(payload);
    },

    changeFilterSlider(payload): void {
      dispatch.events.setFilterSlider(payload);
    },

    async setBetting(payload): Promise<void> {
      if (payload.type === 'regular' || payload.type==='bonus'){
        const secondaryPayload= { type:'regular', event: {} };
        if (payload.type === 'regular'){
          secondaryPayload.type= 'bonus';
        }
        await dispatch.events.setNumberBet(payload);
        await dispatch.events.setNumberBet(secondaryPayload);
      } else {
        await dispatch.events.setNumberBet(payload);
      }
    },

    async resetFilterSports(getList: boolean = true): Promise<void> {
      dispatch.events.setFilterTournaments([]);
      if (getList) {
        dispatch.events.getSportEventsList([]);
      }
    },

    async clearFilterTournaments(): Promise<void> {
      dispatch.events.setFilterTournaments([]);
    },

    async changeFilterSportsType(payload): Promise<void> {
      dispatch.events.setFilterType(payload);
    },

    async setResetPermutations(): Promise<void> {
      await dispatch.events.resetNumberPermutations();
    },

    async resetTree(){
      dispatch.events.setDataTree({ tree: [] });
    },

    async changeFilterSportsHours(payload): Promise<void> {
      dispatch.events.setFilterHours(payload);
    },

    async changeDirtiness(payload: boolean): Promise<void> {
      dispatch.events.setDirtyPage(payload);
    },

    async changeFilterSportsMeeting(payload: [] | string, state): Promise<any> {
      if (_.isArray(payload)) {
        dispatch.events.setFilterMeetings(payload);

        return payload;
      }

      let meetings: any = [...state.events.filterSports.meetings];
      const indexMeeting = state.events.filterSports.meetings.includes(payload?.toString());

      if (!indexMeeting) {
        meetings = [...state.events.filterSports.meetings, payload];
      } else {
        meetings = _.filter(meetings, m => m !== payload.toString());
      }
      dispatch.events.setFilterMeetings(meetings);

      return meetings;
    },

    async changeFilterSportsTournaments(payload, state): Promise<string[]> {
      const codes = [];
      const tournaments = JSON.parse(JSON.stringify(state.events.filterSports.tournaments)) || [];
      const tree = JSON.parse(JSON.stringify(state.events.eventsTree));

      await dispatch.events.resetSearchResults();

      switch (payload.level) {
        case 0: {
          const sport = _.find(tree, s => s.code.toString() === payload.code);
          for (const c of sport.categories) {
            const tournamentCodes = _.reduce(c.tournaments, (prev, curr) => {
              return [...prev, curr.code.toString()];
            }, []);

            codes.push(...tournamentCodes);
          }

          break;
        }

        case 1: {
          const sport = _.find(tree, s => s.code.toString() === payload.sport);
          const category = _.find(sport.categories, c => c.code.toString() === payload.code);

          const tournamentCodes = _.reduce(category.tournaments, (prev, curr) => {
            return [...prev, curr.code.toString()];
          }, []);

          codes.push(...tournamentCodes);
          break;
        }

        case 2:
          codes.push(payload.code.toString());
          break;

        default:
          codes.push(...payload.tournaments);
          break;
      }

      const newTournaments: string[] = !!payload.checked ? _.uniq([...tournaments, ...codes]) : _.pullAll(tournaments, codes);
      dispatch.events.setFilterTournaments(newTournaments);

      if (!_.isUndefined(payload.level)) {
        dispatch.events.setFilterLevel(!!payload.checked ? payload.level : 2);
      }

      if (!isMobile) {
        if (!_.isUndefined(payload.level) && !!payload.checked) {
          dispatch.events.getSportEventsList(codes);
        } else if (!payload.checked) {
          if (newTournaments.length === 0) {
            dispatch.events.getSportEventsList([]);
          } else {
            const newEvents = [];
            const stateEvents = JSON.parse(JSON.stringify(state.events.events));

            for (const event of stateEvents) {
              const tournamentsToBeRemoved = [];
              for (const tournament of event.tournaments) {
                if (newTournaments.indexOf(tournament.code.toString()) === -1) {
                  tournamentsToBeRemoved.push(tournament.code);
                }
              }

              event.tournaments = _.filter(event.tournaments, t => tournamentsToBeRemoved.indexOf(t.code) === -1);

              if (event.tournaments.length > 0) {
                newEvents.push(event);
              }
            }

            dispatch.events.setSportEvents({ events: newEvents });
          }
        }
      }

      return newTournaments;
    },

    async loadNumberBettingTree(hideLoading: boolean = false, state): Promise<void> {
      try {
        const { rangeHours, selectedDate } = state.events.filterSlider;
        if (!hideLoading) {
          dispatch.events.setLoading(true);
        }

        const response = await get(`${ apiUrls.numbersBettingTree }?range=${ rangeHours }&selectedDate=${ selectedDate }&limit=${ 3 }`);

        if (response && response.status === 200) {
          await dispatch.events.setDataTree(response.data.results);
          if (!hideLoading) dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async getRacingList(payload: { hideLoading?: boolean, selectedDate: string }): Promise<void> {
      try {
        if (!payload.hideLoading) dispatch.events.setLoading(true);

        const response = await get(`${ apiUrls.racingEventsList }?selectedDate=${ payload.selectedDate }`);

        if (response && response.status === 200) {
          dispatch.events.setHorseListData(response.data.results);
          dispatch.events.setLoading(false);
        }

        if (!payload.hideLoading) dispatch.events.setLoading(false);

      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async loadRacingTree(payload: { hideLoading?: boolean, mode?: string } = {}, state): Promise<void> {
      try {
        const { rangeHours, selectedDate } = state.events.filterSlider;

        if (!payload.hideLoading) dispatch.events.setLoading(true);

        let response = undefined;

        if (state.filters && state.filters.calendarType === 'calendar') {
          response = await get(`${ apiUrls.racingEventsTree }?mode=${
            payload.mode }&range=${ rangeHours }&selectedDate=${ selectedDate }&limit=3`);
        } else {
          response = await get(`${ apiUrls.racingEventsTree }?mode=${
            payload.mode ? payload.mode : 'antepost' }&range=${ rangeHours }&selectedDate=${ selectedDate }&limit=3`);
        }

        if (response && response.status === 200) {
          const results = response.data.results;
          if (window.location.pathname.includes('/next-races')) {
            // Remove nextEvents from tree array not to override next-races array
            delete results.nextEvents;
          }

          dispatch.events.setDataTree(results);
        }

        if (!payload.hideLoading) dispatch.events.setLoading(false);

      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async permutations(payload): Promise<void> {
      try {
        const response = await post(apiUrls.numberCombinations, payload);
        if (response && response.status ===200){
          if (payload.type !== 'number'){
            await dispatch.events.setPermutations(response.data.results);
          } else {
            await dispatch.events.setSinglePagePermutation(response.data.results);
            await dispatch.events.setPermutations(response.data.results);

          }
        } else {
          const errorResponse =  { anyNumber:[], maximumProfit:0, maximumReturn:0, permutationsCount:0, singleStake:1000, totalStake:0 };
          await dispatch.events.setPermutations(errorResponse);
          await dispatch.events.setSinglePagePermutation(errorResponse);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }

    },

    async getSearchResults(payload: { q: string, module?: string, product?: string }): Promise<void> {
      try {
        dispatch.events.setLoading(true);
        dispatch.events.setSearching({ status: true, product: payload.product });
        if (payload.q){
          const response = await get(`${ apiUrls.search }?q=${ payload.q }${ payload.product ? `&module=${ payload.product }` : '' }`);
          if (response && response.status === 200) {
            await dispatch.events.setSearchResults(response.data.results);
          }
        } else {
          dispatch.events.setSearchResults({ totals: 0 });
        }
        dispatch.events.setLoading(false);
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async searchByEventCode(payload: { code: string }, rootState): Promise<void> {
      try {
        dispatch.events.setLoading(true);
        dispatch.events.setSearching({ status: true, product: 'sports' });
        const typeOfCode = rootState.peripherals.typeOfCode;
        const response = await get(`${ apiUrls.searchEventCode }?eventCode=${ payload.code }&typeOfCode=${ typeOfCode }`);

        if (response && response.status === 200) {
          await dispatch.events.setSearchResults(response.data.results);
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    resetSearchResults(): void {
      dispatch.events.setSearching(false);
      dispatch.events.setSearchResults({});
      dispatch.events.setSearch('');
      dispatch.events.setCardCodeEvent(false);
    },

    async getSportEvents(payload, state): Promise<void> {
      try {
        const { type, rangeHours, tournaments, top } = state.events.filterSports;
        dispatch.events.setSportEventsLoading(true);

        const topFilter = !top ? '' : `&top=${ top }`;
        let mode = 'prematch';
        if (type === '2,8') {
          mode = 'live';
        } else if (type === '4') {
          mode = 'outright';
        }

        let url = apiUrls.sportEvents + `?type=${ _.sumBy(type.split(','), (t) => Number(t))
        }&range=${ rangeHours }&tournaments=${ _.join(tournaments, ',') }&mode=${ mode }`;

        if (isMobile){
          url = apiUrls.sportEvents;
        }
        const response = await get(url);

        if (response && response.status === 200 && (!isMobile && topFilter === '')) {
          dispatch.events.setSportEvents(response.data.results);
          dispatch.events.setSportEventsLoading(false);
        } else {
          dispatch.events.setSportEventsLoading(false);
        }
      } catch (error) {
        dispatch.events.setSportEventsLoading(false);
        // TODO: HANDLE ERROR
      }
    },

    async getPopularNumberEvents(): Promise<void> {
      try {
        dispatch.events.setLoading(true);
        const response = await get(apiUrls.popularEvents);
        if (response && response.status === 200) {
          await dispatch.events.setPopularEvents(response.data.results[0].tournaments);
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async getSportEventsTree(payload, state): Promise<void> {
      try {
        const { type, rangeHours } = state.events.filterSports;
        let mode = 'prematch';
        if (type === '2,8') {
          mode = 'live';
        } else if (type === '4') {
          mode = 'outright';
        }

        dispatch.events.setSportEventsTreeLoading(true);

        const response = await get(apiUrls.sportEventsTree + `?mode=${ mode }&range=${ rangeHours }`);

        if (response && response.status === 200) {
          dispatch.events.setSportEventsTree(response.data.results);
          dispatch.events.setSportEventsTreeLoading(false);
        } else {
          dispatch.events.setSportEventsTree([]);
          dispatch.events.setSportEventsTreeLoading(false);
        }
      } catch (error) {
        dispatch.events.setSportEventsTree([]);
        dispatch.events.setSportEventsTreeLoading(false);
        // TODO: HANDLE ERROR
      }
    },

    async getSportEventsList(payload: string[], state): Promise<void> {
      const { type, rangeHours, tournaments } = state.events.filterSports;

      try {
        dispatch.events.setLoading(true);
        dispatch.events.setSport(null);

        let mode = 'prematch';
        if (type === '2,8') {
          mode = 'live';
        } else if (type === '4') {
          mode = 'outright';
        }

        let url = apiUrls.sportEventsList +
        `?type=${ _.sumBy(type.split(','), (t) => Number(t)) }&range=${ rangeHours }&tournaments=${
          _.join(payload ? payload : tournaments, ',') }&mode=${ mode }`;

        if (isMobile){
          url = apiUrls.sportEventsList;
        }

        const response = await get(url);

        if (response && response.status === 200) {
          const stateEvents = _.reduce(JSON.parse(JSON.stringify(state.events.events)), (prev, curr) => {
            curr.tournaments = _.filter(curr.tournaments, tournament => tournaments.indexOf(tournament.code.toString()) !== -1);
            if (curr.tournaments.length > 0) {
              prev.push(curr);
            }

            return prev;
          }, []);

          for (const res of response.data.results) {
            const foundSport = _.find(stateEvents, e => e.code === res.code);

            if (foundSport) {
              for (const tournament of res.tournaments) {
                const foundTournament = _.find(foundSport.tournaments, t => t.code === tournament.code);

                if (!foundTournament) {
                  foundSport.tournaments.unshift(tournament);
                } else {
                  const diff = _.difference(tournament.events, foundTournament.events);
                  for (const tournament of foundSport.tournaments) {
                    tournament.events = { ...tournament.events, ...diff };
                  }
                }
              }
            } else {
              stateEvents.unshift(res);
            }
          }
          dispatch.events.setSportEvents({ events: stateEvents });
          dispatch.events.setLoading(false);
        } else {
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
        dispatch.events.setLoading(false);
      }
    },

    async getSportEventsListBySport(payload: number, state): Promise<void> {
      const { type } = state.events.filterSports;

      try {
        dispatch.events.setLoading(true);
        dispatch.events.setSport(payload || null);
        const response = await get(apiUrls.sportEventsList +
          `?type=${ _.sumBy(type.split(','), (t) => Number(t)) }&range=24&sports=${ payload || '' }`
        );

        if (response && response.status === 200) {
          dispatch.events.setSportEvents({ events: response.data.results });

          dispatch.events.setLoading(false);
        } else {
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
        dispatch.events.setLoading(false);
      }
    },

    async getSportEventByCode(payload: string, state): Promise<void> {
      try {
        dispatch.events.setEvent({});

        if (payload !== state.events.selectedEvent?.code) {
          const response = await get(replaceUrls(apiUrls.sportEventByCode, { code: payload }));

          if (response && response.status === 200) {
            await dispatch.events.setEvent(response.data.results);
          }
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async getSportSearchResults(payload, state): Promise<void> {
      try {
        dispatch.events.setLoading(true);
        dispatch.events.setSearching(payload !== '');
        const response = await get(
          `${ apiUrls.sportEventsList }?type=${ _.sumBy(state.events.filterSports.type.split(','), (t) => Number(t)) }&searchpattern=${ payload }`
        );
        await dispatch.events.resetFilterSports();

        if (response && response.status === 200) {
          await dispatch.events.setSportEvents({ events: response.data.results });
          dispatch.events.setLoading(false);
        } else {
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
        dispatch.events.setLoading(false);
      }
    },

    async getEventsByTop(payload: string, state): Promise<void> {
      try {
        dispatch.events.setSelectedTop(payload);

        dispatch.events.setLoading(true);
        await dispatch.events.resetSearchResults();
        const response = await get(
          `${ apiUrls.sportEventsList }?type=${ _.sumBy(state.events.filterSports.type.split(','), (t) => Number(t)) }&top=${ payload }`
        );

        if (response && response.status === 200) {
          if (isMobile) {
            await dispatch.filters.setFilterResults(response.data.results);
          } else {
            await dispatch.events.setSportEvents({ events: response.data.results });
          }
          dispatch.events.setLoading(false);
        } else {
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
        dispatch.events.setLoading(false);
      }
    },

    async getAvailableSports(payload, state): Promise<void> {
      try {
        let mode = 'prematch';
        if (state.events.filterSports.type === '2,8') {
          mode = 'live';
        } else if (state.events.filterSports.type === '4') {
          mode = 'outright';
        }

        const response = await get(`${ apiUrls.availableSports }?mode=${ mode }&hours=24`);

        if (response && response.status === 200) {
          dispatch.events.setAvailableSports(response.data.results.sports);
          dispatch.events.setSportEvents(response.data.results);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async searchBetCode(payload, rootState): Promise<void> {
      try {
        const { code, productType, betCode } = payload;
        const typeOfCode = rootState.peripherals.typeOfCode;
        const response = await get(`${ apiUrls.searchBetCode }?code=${ code }&productType=${ productType }&betCode=${ betCode
        }&typeOfCode=${ typeOfCode }`);
        if (response && response.status === 200) {
          return response.data.results;
        }
      } catch (error) {
        return null;
      }
    },

    async fastInput(payload, rootState){
      try {
        const { productType, betType, race, couponCard } = payload;
        const typeOfCode = rootState.peripherals.typeOfCode;

        const response = await get(
          `${ apiUrls.searchByCoupon }?couponCard=${ couponCard }&productType=${ productType }&betType=${ betType }&race=${ race
          }&typeOfCode=${ typeOfCode }`
        );

        return response.data.results;
      } catch (error) {
        return null;
      }
    },

    async racingBetCodeSearch(payload, rootState){
      try {
        const { productType, betType, race, couponCard } = payload;
        const typeOfCode = rootState.peripherals.typeOfCode;

        const response = await get(
          `${ apiUrls.searchByCoupon }?couponCard=${ couponCard }&productType=${ productType }&betType=${ betType }&race=${ race
          }&typeOfCode=${ typeOfCode }`
        );
        const { results } = response.data;

        if (results.error){
          dispatch(push(`/racing/${ results.meetingInfos?.meetingCode }/${ results.code }`));
          return 'invalidOutcome';
        } else {
          dispatch(push(`/racing/${ results.meetingInfos?.meetingCode }/${ results.code }/all/${ results.markets.markets[0].code }`));
          return '';
        }

      } catch (error) {
        return null;
      }
    },

    changeCardCodeError(payload:string){
      dispatch.events.setCardCodeErrors(payload);
    },

    changeCardCodeEvent(payload){
      dispatch.events.setCardCodeEvent(payload);
    },

    async getEventCodes(payload) {
      try {
        const response = await get(`${ apiUrls.couponList }?productType=${ payload }`);
        if (response && response.status === 200) {
          return response.data.results;
        }
        return null;
      } catch (e) {
        return null;
      }
    },

    changeCardCodes(payload){
      dispatch.events.setCardCodes(payload);
    },

    async getBetCodes(payload){
      try {
        const response = await get(`${ apiUrls.betCodes }?productType=${ payload }`);
        if (response.status === 200){
          return response.data.results;
        }
      } catch (error) {
        //TODO: handle error
      }
    },

    async getNumberBetCodes(){
      try {
        const response = await get(`${ apiUrls.betCodes }?productType=4&isList=true`);
        if (response.status === 200){
          return response.data.results;
        }
      } catch (error) {
        //TODO: handle error
      }
    },

    updateEvent(payload, state){
      const events = JSON.parse(JSON.stringify(state.events.events));
      const receivedEvents = JSON.parse(payload);
      let changed = false;
      for (const outcome of receivedEvents){
        for (const sport of events){
          for (const tournament of sport.tournaments){
            const eventIndex = _.findIndex(tournament.events, { eventCode: outcome.id });
            if (eventIndex >= 0){
              for (const mainMarket of tournament.events[eventIndex].mainMarkets){
                const outcomeIndex = _.findIndex(mainMarket.outcomeCode, (outcomeId) => outcomeId === outcome.outcomes.id);
                if (outcomeIndex >=0){
                  if (mainMarket.outcomeOdds[outcomeIndex] !== outcome.outcomes.odds){
                    if (!mainMarket.oddsChanged){
                      mainMarket.oddsChanged = new Array(mainMarket.outcomeOdds.length).fill(null);
                    }
                    mainMarket.oddsChanged[outcomeIndex] = mainMarket.outcomeOdds[outcomeIndex] > outcome.outcomes.odds ? 'decreased' : 'increased';
                    mainMarket.outcomeOdds[outcomeIndex] = outcome.outcomes.odds;
                    changed = true;
                  }
                }
              }
            }
          }
        }
      }
      if (changed){
        dispatch.events.setUpdatedEvents(events);
      }
    },

    toggleMatchTracker(payload): void {
      dispatch.events.setMatchTracker(payload);
    },

    changeMobileTracker(){
      dispatch.events.setShowMobileTracker();
    },

    async changeValues (payload, state) {
      const { key, value } = payload;
      const values = { ...state.events.sportValues, [key]: value };
      dispatch.events.setValues(values);
    },

    async changeErrors (payload, state) {
      const { value, error } = payload;

      if (value === ''){
        dispatch.events.setErrors({ betcode:'', race:'', betType:'', numbers:'', stake:'' });
      } else {
        const errors = { ...state.eventsErrors, [value]: error };
        dispatch.events.setErrors(errors);
      }
    },

    addToFastInputHistory(payload){
      dispatch.events.setSportHistory(payload);
    },

    resetFastModeHistory(){
      dispatch.events.setResetHistory();
    },

    addEventToLiveOutcomes(payload : number[]): void {
      dispatch.events.setLiveOutcomes(payload);
    },

    updateAllMarkets(payload, state) {
      const event = JSON.parse(JSON.stringify(state.events.selectedEvent));
      const eventMarkets = event.markets;
      let changed = false;
      for (const outcome of JSON.parse(payload)){
        for (const marketType of eventMarkets){
          for (const markets of marketType.markets){

            const outcomeIndex = _.findIndex(markets.outcomes, { code: outcome.outcomes.id });
            if (outcomeIndex >=0){
              if (markets.outcomes[outcomeIndex].value !== outcome.outcomes.odds){
                markets.outcomes[outcomeIndex].oddsChanged = markets.outcomes[outcomeIndex].value > outcome.outcomes.odds ? 'decreased' : 'increased';
                markets.outcomes[outcomeIndex].value = outcome.outcomes.odds;
                changed = true;
              }
            }
          }
        }
      }
      if (changed){
        event.markets = eventMarkets;
        dispatch.events.setNewAllMarkets(event);
      }
    },

    removeFromLiveOutcomes(payload : number[]): void {
      dispatch.events.removeLiveOutcomes(payload);
    },

    setResetLiveOutcomes(){
      dispatch.events.resetLiveOutcomes();
    },

    changeShouldReloadTabs(payload){
      dispatch.events.setShouldReloadTabs(payload);
    }
    // streamEvent(id) {
    //   dispatch.events.clearEventSource();

    //   const eventSource = new EventSource(process.env.REACT_APP_BASE_URL + apiUrls.serverSentEvent.replace(':id', id));

    //   eventSource.onmessage = e => {
    //     dispatch.events.setEvent(JSON.parse(e.data));
    //   };

    //   let connAttempts = 0;
    //   eventSource.onerror = e => {
    //     if (e.target && (e.target.readyState === eventSource.CONNECTING || e.target.readyState === eventSource.CLOSED)) {
    //       connAttempts++;

    //       if (connAttempts >= 5) {
    //         eventSource.close();

    //         dispatch.toast.showToast({ type: 'error', message: 'Unable to establish connection to the server!' });
    //       }
    //     }
    //   };

    //   // eventSource.addEventListener('message', (e) => {
    //   //   console.log(e);
    //   // });

    //   dispatch.events.setEventSource(eventSource);
    // }
  })
});

